import storage from '../storage';

const Cookies =
  process.env.APP_PLATFORM === 'web' ? require('universal-cookie') : null;

const messages =
  // (typeof window === 'undefined') || process.env.NODE_ENV === 'development' || process.env.APP_PLATFORM === 'web'
  process.env.NODE_ENV === 'development' || process.env.APP_PLATFORM === 'web'
    ? require('../../public/_locales/en/messages.json')
    : null;

const cookies =
  Cookies && typeof window !== 'undefined' ? new Cookies.default() : null;

const getLocalMessage = (key) => (messages ? messages[key]?.message : key);

export const checkChrome = (prop: string): boolean =>
  typeof chrome !== 'undefined' && typeof chrome[prop] !== 'undefined';

export const sendMessage = (request: unknown): Promise<void> =>
  new Promise((resolve) =>
    checkChrome('runtime')
      ? chrome.runtime.sendMessage(request, resolve)
      : resolve(null)
  );

export const queryTabs = (
  query: chrome.tabs.QueryInfo
): Promise<chrome.tabs.Tab[]> =>
  new Promise((resolve) => {
    if (checkChrome('tabs')) return chrome.tabs.query(query, resolve);
    if (process.env.NODE_ENV === 'development') {
      return import('./tabs-stub.json').then((json) => {
        resolve(json.default);
      });
    }

    return resolve([]);
  });

export const updateTab = (
  tabId: number,
  updateProperties: chrome.tabs.UpdateProperties
): Promise<void> =>
  new Promise((resolve) =>
    checkChrome('tabs')
      ? chrome.tabs.update(tabId, updateProperties, () => resolve(null))
      : resolve(null)
  );

export const removeStorageItems = (items: string[]): Promise<null> => {
  if (checkChrome('storage')) {
    chrome.storage.local.remove(items);
  }
  return Promise.resolve(null);
};

export const getStorageItems = <T>(items = null): Promise<T> => {
  if (checkChrome('storage')) {
    return new Promise((resolve) => chrome.storage.local.get(items, resolve));
  }

  const keys = items || Object.keys(localStorage);
  return Promise.resolve(
    keys.reduce(
      (acc, key) => ({
        ...acc,
        [key]: storage.getItem(key),
      }),
      {}
    )
  );
};

export const setStorageItems = (
  items: Record<string, unknown>
): Promise<null> => {
  if (checkChrome('storage')) {
    chrome.storage.local.set(items);
  } else {
    Object.keys(items).forEach((key) => {
      storage.setItem(key, items[key]);
    });
  }

  return Promise.resolve(null);
};

export const getTranslation = (key: string): string =>
  checkChrome('i18n') ? chrome.i18n.getMessage(key) : getLocalMessage(key);

export const getUILanguage = (): string =>
  checkChrome('i18n') ? chrome.i18n.getUILanguage() : 'en-US';

export const setCookie = (params: chrome.cookies.SetDetails): void => {
  if (checkChrome('cookies')) {
    chrome.cookies.set(params);
  } else {
    cookies.set(params.name, params.value, {
      path: '/',
      expires: new Date(params.expirationDate * 1000),
      secure: params.secure,
      sameSite: params.sameSite,
    });
  }
};

export const getCookie = (
  params: chrome.cookies.Details
): Promise<{ value: string }> =>
  new Promise((resolve) => {
    if (checkChrome('cookies')) {
      return chrome.cookies.get(params, resolve);
    }
    const value = cookies.get(params.name);
    resolve(value ? { value } : null);
  });

export const createNewTab = (url: string): Promise<unknown> =>
  new Promise((resolve) =>
    checkChrome('tabs') ? chrome.tabs.create({ url }, resolve) : resolve(null)
  );

export const removeTab = (id: number): Promise<unknown> =>
  new Promise((resolve) =>
    checkChrome('tabs') ? chrome.tabs.remove(id, resolve) : resolve(null)
  );

export interface FormattedTab {
  term: string;
  url?: string;
  tabId: number;
  favIconUrl?: string;
}

export const getTopSites = (): Promise<chrome.topSites.MostVisitedURL[]> =>
  new Promise((resolve) => {
    if (checkChrome('tabs')) return chrome.topSites.get(resolve);
    if (process.env.NODE_ENV === 'development') {
      return import('./topsites-stub.json').then((json) => {
        resolve(json.default);
      });
    }
    resolve(null);
  });

export const requestFileSystem = (size?: number): Promise<FileSystem> =>
  new Promise((resolve, reject) =>
    typeof window.webkitRequestFileSystem === 'undefined'
      ? reject()
      : window.webkitRequestFileSystem(window.PERSISTENT, size, resolve, reject)
  );

export const getFile = (
  fs: FileSystem,
  fileURL: string,
  config: Flags
): Promise<FileEntry> =>
  new Promise((resolve, reject) => {
    fs.root.getFile(fileURL, config, resolve, reject);
  });

export const removeFile = (fs: FileSystem, fileURL: string): Promise<null> =>
  getFile(fs, fileURL.split('/').pop(), { create: false }).then(
    (fileEntry) =>
      new Promise((resolve, reject) =>
        fileEntry.remove(() => resolve(null), reject)
      )
  );

export const getCommands = (): Promise<chrome.commands.Command[]> =>
  new Promise((resolve) =>
    checkChrome('commands') ? chrome.commands.getAll(resolve) : resolve([])
  );
